import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { calculateBilling } from '@/pages/billing/services/billing.services';
import { saveInvoice } from '@/pages/billing/services/billingInvoices.services';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

const toISODate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
const fromISODateOnly = (iso) => {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const Currency = ({ value }) => <span>${(Number(value || 0)).toLocaleString('es-CO')}</span>;

export default function LiquidacionPage() {
  const { user, ROLES } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [startDate, setStartDate] = useState(toISODate(firstDay));
  const [endDate, setEndDate] = useState(toISODate(lastDay));
  const [laundries, setLaundries] = useState([]);
  const [idLavanderia, setIdLavanderia] = useState(() => user?.lavanderia?._id || user?.lavanderia?.id || ''); // backward for single
  const [lavanderiaIds, setLavanderiaIds] = useState(() => {
    const fixed = user?.lavanderia?._id || user?.lavanderia?.id;
    return fixed ? [fixed] : [];
  });

  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [reference, setReference] = useState('');

  // Section includes
  const [includeKilos, setIncludeKilos] = useState(true);
  const [includeWorked, setIncludeWorked] = useState(true);
  const [includeAdmins, setIncludeAdmins] = useState(true);

  // Per-laundry overrides for receptions: fixed tariff and discount
  const [receptionOverrides, setReceptionOverrides] = useState({});

  // Exclusions (ids)
  const [excludedWorked, setExcludedWorked] = useState(() => new Set());
  const [excludedAdmins, setExcludedAdmins] = useState(() => new Set());
  const [excludedSupervisors, setExcludedSupervisors] = useState(() => new Set());

  const canQuery = useMemo(() => (lavanderiaIds && lavanderiaIds.length > 0), [lavanderiaIds]);

  useEffect(() => {
    const load = async () => {
      try {
        if (user?.role === ROLES.CENTRO_LAVADO || user?.role === ROLES.SUPERVISOR) return;
        const resp = await fetchLaundries({ query: { estado: 'Activo' }, limit: 1000 });
        setLaundries(resp?.data || []);
      } catch (e) {}
    }
    load();
  }, [user, ROLES]);

  // Handlers to toggle exclusions and recompute live
  const toggleSet = (setter) => (id, checked) => {
    setter(prev => {
      const next = new Set(prev);
      const key = String(id);
      if (checked) next.add(key); else next.delete(key);
      return next;
    });
  };

  // Create a print-friendly window to allow the user to download as PDF
  function generateLiquidacionPDF(inv, ctx = {}) {
    if (!inv) return;
    const fmt = (n) => `$${Number(n||0).toLocaleString('es-CO')}`;
    const getLaundry = (id) => (laundries||[]).find(l => (l._id||l.id) === id)?.nombre || id || '';
    const itemsRows = (inv.items||[]).map(it => `
      <tr>
        <td>${it.description}</td>
        <td style="text-align:right">${it.quantity}</td>
        <td style="text-align:right">${fmt(it.unitPrice)}</td>
        <td style="text-align:right">${fmt(it.amount)}</td>
      </tr>
    `).join('');

    const recRows = ((inv.details?.receptionsByLaundry)||[]).map(r => {
      const unit = 5;
      const kilos = Number(r.kilos||0);
      const base = kilos * unit;
      const ov = inv.details?.receptionOverrides?.[r.lavanderiaId] || {};
      const fixed = Number(ov?.fixedAmount||0);
      const sub = base;
      const disc = ov?.fixedEnabled ? Math.max(0, base - fixed) : Number(ov?.discount||0);
      const total = ov?.fixedEnabled ? fixed : Math.max(0, base - disc);
      return `
        <tr>
          <td>${getLaundry(r.lavanderiaId)}</td>
          <td style="text-align:right">${kilos}</td>
          <td style="text-align:right">${fmt(unit)}</td>
          <td style="text-align:right">${fmt(sub)}</td>
          <td style="text-align:right">${fmt(disc)}</td>
          <td style="text-align:right">${fmt(total)}</td>
        </tr>
      `;
    }).join('');

    const workedRows = (inv.details?.workedUsers||[]).map(u => {
      const excluded = !!u.excluded;
      const disc = excluded ? 5000 : 0;
      const total = excluded ? 0 : 5000;
      return `
        <tr>
          <td>${u.nombre||''}</td>
          <td>${u.cedula||'-'}</td>
          <td>${u.rol||'-'}</td>
          <td style="text-align:right">${u.count||0}</td>
          <td style="text-align:right">${fmt(disc)}</td>
          <td style="text-align:right">${fmt(total)}</td>
        </tr>
      `;
    }).join('');

    const adminRows = (inv.details?.adminUsers||[]).map(u => `
      <tr>
        <td>${u.nombre||''}</td>
        <td>${u.correo||'-'}</td>
      </tr>
    `).join('');
    const supRows = (inv.details?.supervisorUsers||[]).map(u => `
      <tr>
        <td>${u.nombre||''}</td>
        <td>${u.correo||'-'}</td>
      </tr>
    `).join('');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Liquidación ${ctx.reference||''}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111; }
            h1, h2 { margin: 0 0 8px; }
            h1 { font-size: 18px; }
            h2 { font-size: 16px; margin-top: 24px; }
            .muted { color: #666; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
            th { background: #f5f5f5; text-align: left; }
            .right { text-align: right; }
            .totals { margin-top: 8px; }
          </style>
        </head>
        <body>
          <h1>Liquidación ${ctx.reference ? `- ${ctx.reference}` : ''}</h1>
          <div class="muted">Periodo: ${String(inv.period?.from||'').slice(0,10)} a ${String(inv.period?.to||'').slice(0,10)}</div>

          <h2>Resumen</h2>
          <table>
            <thead>
              <tr>
                <th>Concepto</th>
                <th class="right">Cantidad</th>
                <th class="right">Precio Unitario</th>
                <th class="right">Valor</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3"><strong>Subtotal</strong></td>
                <td class="right"><strong>${fmt(inv.subtotal)}</strong></td>
              </tr>
              <tr>
                <td colspan="3">IVA</td>
                <td class="right">${fmt(inv.taxes?.iva||0)}</td>
              </tr>
              <tr>
                <td colspan="3"><strong>Total</strong></td>
                <td class="right"><strong>${fmt(inv.total)}</strong></td>
              </tr>
            </tfoot>
          </table>

          <h2>Kilos recepcionados (por lavandería)</h2>
          <table>
            <thead>
              <tr>
                <th>Descripción</th>
                <th class="right">Kilos</th>
                <th class="right">V.Unit</th>
                <th class="right">Subtotal</th>
                <th class="right">Descuento</th>
                <th class="right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${recRows}
            </tbody>
          </table>

          <h2>Usuarios con registro de horas</h2>
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Documento</th>
                <th>Rol</th>
                <th class="right">Fichajes</th>
                <th class="right">Descuento</th>
                <th class="right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${workedRows}
            </tbody>
          </table>

          <h2>Usuarios administradores</h2>
          <table>
            <thead>
              <tr><th>Nombre</th><th>Correo</th></tr>
            </thead>
            <tbody>
              ${adminRows}
            </tbody>
          </table>

          <h2>Usuarios supervisores</h2>
          <table>
            <thead>
              <tr><th>Nombre</th><th>Correo</th></tr>
            </thead>
            <tbody>
              ${supRows}
            </tbody>
          </table>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  }
  const onToggleWorked = toggleSet(setExcludedWorked);
  const onToggleAdmin = toggleSet(setExcludedAdmins);
  const onToggleSupervisor = toggleSet(setExcludedSupervisors);

  const adjustedInvoice = useMemo(() => buildAdjustedInvoice(invoice), [invoice, excludedWorked, excludedAdmins, excludedSupervisors, includeKilos, includeWorked, includeAdmins, receptionOverrides, lavanderiaIds]);

  // Helpers
  const getLaundryName = (id) => {
    const l = (laundries || []).find(x => (x._id || x.id) === id);
    return l?.nombre || id;
  };

  const handleCalculate = async () => {
    if (!canQuery) return;
    setLoading(true);
    try {
      const s = fromISODateOnly(startDate);
      const e = fromISODateOnly(endDate);
      const endOfDay = new Date(e);
      endOfDay.setHours(23, 59, 59, 999);
      const result = await calculateBilling({ lavanderiaIds, from: s.toISOString(), to: endOfDay.toISOString() });
      setInvoice(result);
    } catch (err) {
      console.error('liquidacion error', err);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToFacturacion = async () => {
    if (!invoice) return;
    const adjusted = buildAdjustedInvoice(invoice);
    const payloadMeta = {
      reference: reference || `LIQ-${new Date().toISOString().slice(0,10)}`,
      lavanderiaIds,
    };
    const saved = await saveInvoice({ invoice: adjusted, meta: payloadMeta });
    // Generate a print-friendly PDF view
    try {
      generateLiquidacionPDF(adjusted, { reference: saved?.meta?.reference || payloadMeta.reference, laundries: laundries });
    } catch (e) { /* ignore pdf errors */ }
    toast({ title: 'Liquidación guardada', description: 'Se migró a Facturación.' });
    navigate('/facturacion');
    return saved;
  };

  useEffect(() => {
    handleCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lavanderiaIds, startDate, endDate]);

  // Build adjusted invoice based on exclusions
  function buildAdjustedInvoice(inv) {
    if (!inv) return null;
    const PRICE_WORKED_USER = 5000;
    const PRICE_RECEPTION_KG = 5;
    const PRICE_ROLE = 10000;

    const workedUsers = inv?.details?.workedUsers || [];
    const adminUsers = inv?.details?.adminUsers || [];
    const supervisorUsers = inv?.details?.supervisorUsers || [];

    const workedWithFlags = workedUsers.map(u => ({
      ...u,
      excluded: excludedWorked.has(String(u.id)) || !includeWorked,
    }));
    const includedWorked = workedWithFlags.filter(u => !u.excluded);
    const includedAdmins = includeAdmins ? adminUsers.filter(u => !excludedAdmins.has(String(u.id))) : [];
    const includedSupervisors = includeAdmins ? supervisorUsers.filter(u => !excludedSupervisors.has(String(u.id))) : [];

    const kilosItem = inv.items.find(i => i.code === 'RECEPTIONS_KG');
    const kilosQty = includeKilos ? Number(kilosItem?.quantity || 0) : 0;

    // Compute receptions total considering per-laundry overrides
    const receptions = (inv?.details?.receptionsByLaundry || []).filter(r => lavanderiaIds.includes(r.lavanderiaId));
    const receptionsAmount = includeKilos ? receptions.reduce((sum, r) => {
      const kilos = Number(r.kilos || 0);
      const unit = PRICE_RECEPTION_KG;
      const base = Number((kilos * unit).toFixed(2));
      const ov = receptionOverrides[r.lavanderiaId] || {};
      const fixedAmount = Number(ov.fixedAmount || 0);
      let total;
      if (ov.fixedEnabled) {
        // Total debe ser la tarifa fija; descuento = base - fija; subtotal = base
        total = fixedAmount;
      } else {
        const discount = Number(ov.discount || 0);
        total = Math.max(0, Number((base - discount).toFixed(2)));
      }
      return sum + total;
    }, 0) : 0;

    const items = [
      {
        code: 'WORKED_HOURS_USERS',
        description: 'Usuarios que registraron horas (con salida)',
        quantity: includedWorked.length,
        unitPrice: PRICE_WORKED_USER,
        amount: includedWorked.length * PRICE_WORKED_USER,
      },
      {
        code: 'RECEPTIONS_KG',
        description: 'Kilos recepcionados',
        quantity: kilosQty,
        unitPrice: PRICE_RECEPTION_KG,
        amount: Number(receptionsAmount.toFixed(2)),
      },
      {
        code: 'USERS_ADMIN',
        description: 'Usuarios admin activos',
        quantity: includedAdmins.length,
        unitPrice: PRICE_ROLE,
        amount: includedAdmins.length * PRICE_ROLE,
      },
      {
        code: 'USERS_SUPERVISOR',
        description: 'Usuarios supervisor activos',
        quantity: includedSupervisors.length,
        unitPrice: PRICE_ROLE,
        amount: includedSupervisors.length * PRICE_ROLE,
      },
    ];
    const subtotal = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    const taxes = { iva: 0 };
    const total = subtotal + taxes.iva;
    return {
      ...inv,
      items,
      subtotal,
      taxes,
      total,
      details: {
        ...inv.details,
        workedUsers: workedWithFlags,
        adminUsers: includedAdmins,
        supervisorUsers: includedSupervisors,
        receptionOverrides,
      },
      exclusions: {
        worked: Array.from(excludedWorked),
        admins: Array.from(excludedAdmins),
        supervisors: Array.from(excludedSupervisors),
      }
    };
  };

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Liquidación</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-sm block mb-1">Desde</label>
              <Input type="date" value={String(startDate).slice(0,10)} onChange={(e)=>setStartDate(`${e.target.value}T00:00:00.000Z`)} />
            </div>
            <div>
              <label className="text-sm block mb-1">Hasta</label>
              <Input type="date" value={String(endDate).slice(0,10)} onChange={(e)=>setEndDate(`${e.target.value}T00:00:00.000Z`)} />
            </div>
            <div>
              <label className="text-sm block mb-1">Lavanderías</label>
              {(user?.role === ROLES.CENTRO_LAVADO || user?.role === ROLES.SUPERVISOR) ? (
                <Input disabled value={user?.lavanderia?.nombre || ''} />
              ) : (
                <div className="border rounded p-2 max-h-40 overflow-auto space-y-1">
                  <div className="flex gap-2 mb-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setLavanderiaIds((laundries||[]).map(l => l._id||l.id))}>Seleccionar todos</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setLavanderiaIds([])}>Limpiar</Button>
                  </div>
                  {(laundries||[]).map(l => {
                    const id = l._id || l.id;
                    const checked = lavanderiaIds.includes(id);
                    return (
                      <label key={id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={checked} onChange={(e)=>{
                          setLavanderiaIds(prev => {
                            const set = new Set(prev);
                            if (e.target.checked) set.add(id); else set.delete(id);
                            return Array.from(set);
                          });
                        }} />
                        <span>{l.nombre}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm block mb-1">Referencia</label>
              <Input placeholder="Ej: SEP-2025 / Cliente X" value={reference} onChange={(e)=>setReference(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCalculate} disabled={!canQuery || loading}>{loading ? 'Calculando...' : 'Calcular'}</Button>
              <Button variant="secondary" onClick={handleSaveToFacturacion} disabled={!invoice}>Guardar</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sección: Kilos recepcionados */}
      <Card>
        <CardHeader>
          <CardTitle>Kilos recepcionados</CardTitle>
        </CardHeader>
        <CardContent>
          {!invoice ? (
            <div className="text-sm text-muted-foreground">Sin datos para mostrar.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm">Periodo: <span className="font-medium">{String(adjustedInvoice.period.from).slice(0,10)} a {String(adjustedInvoice.period.to).slice(0,10)}</span></div>
                {(() => {
                  const it = (adjustedInvoice.items||[]).find(i => i.code === 'RECEPTIONS_KG');
                  return <div className="text-right text-sm">Kg: <span className="font-semibold">{it?.quantity ?? 0}</span> • Valor: <span className="font-semibold"><Currency value={it?.amount ?? 0} /></span></div>;
                })()}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input id="inc-kilos" type="checkbox" checked={includeKilos} onChange={(e)=>setIncludeKilos(e.target.checked)} />
                <label htmlFor="inc-kilos">Incluir kilos recepcionados</label>
              </div>
              {/* Breakdown per lavandería */}
              <div className="overflow-x-auto border rounded">
                <table className="min-w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 text-xs uppercase text-muted-foreground">Descripción</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Kilos</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Valor unitario</th>
                      <th className="text-center p-2 text-xs uppercase text-muted-foreground">Fija</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Tarifa Mínima</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Subtotal</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Descuento</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((adjustedInvoice?.details?.receptionsByLaundry)||[])
                      .filter(r => lavanderiaIds.includes(r.lavanderiaId))
                      .map(r => {
                        const kilos = includeKilos ? Number(r.kilos || 0) : 0;
                        const unit = 5;
                        const override = receptionOverrides[r.lavanderiaId] || {};
                        const base = Number((kilos * unit).toFixed(2));
                        const fixed = Number(override.fixedAmount || 0);
                        const subtotal = base;
                        const discount = override.fixedEnabled ? Math.max(0, Number((base - fixed).toFixed(2))) : Number(override.discount || 0);
                        const total = override.fixedEnabled ? fixed : Math.max(0, Number((base - discount).toFixed(2)));
                        return (
                          <tr key={r.lavanderiaId} className="border-t">
                            <td className="p-2 text-sm">{getLaundryName(r.lavanderiaId)}</td>
                            <td className="p-2 text-sm text-right">{kilos}</td>
                            <td className="p-2 text-sm text-right"><Currency value={unit} /></td>
                            <td className="p-2 text-sm text-center">
                              <input
                                type="checkbox"
                                checked={!!override.fixedEnabled}
                                onChange={(e)=>{
                                  const enabled = e.target.checked;
                                  setReceptionOverrides(prev => ({
                                    ...prev,
                                    [r.lavanderiaId]: {
                                      ...(prev[r.lavanderiaId] || {}),
                                      fixedEnabled: enabled,
                                      fixedAmount: enabled ? (prev[r.lavanderiaId]?.fixedAmount ?? 100000) : (prev[r.lavanderiaId]?.fixedAmount ?? ''),
                                    }
                                  }));
                                }}
                              />
                            </td>
                            <td className="p-2 text-sm text-right">
                              <input
                                type="number"
                                className="w-28 border rounded px-2 h-8 text-right"
                                disabled={!override.fixedEnabled}
                                value={override.fixedAmount ?? ''}
                                onChange={(e)=>{
                                  const val = e.target.value === '' ? '' : Number(e.target.value);
                                  setReceptionOverrides(prev => ({
                                    ...prev,
                                    [r.lavanderiaId]: {
                                      ...(prev[r.lavanderiaId] || {}),
                                      fixedAmount: val,
                                    }
                                  }));
                                }}
                              />
                            </td>
                            <td className="p-2 text-sm text-right"><Currency value={subtotal} /></td>
                            <td className="p-2 text-sm text-right">
                              {override.fixedEnabled ? (
                                // Descuento calculado = base - fija (solo lectura)
                                <Currency value={Math.max(0, Number(((kilos * unit) - Number(override.fixedAmount || 0)).toFixed(2)))} />
                              ) : (
                                <input
                                  type="number"
                                  className="w-28 border rounded px-2 h-8 text-right"
                                  value={override.discount || ''}
                                  onChange={(e)=>{
                                    const val = e.target.value === '' ? '' : Number(e.target.value);
                                    setReceptionOverrides(prev => ({
                                      ...prev,
                                      [r.lavanderiaId]: {
                                        ...(prev[r.lavanderiaId] || {}),
                                        discount: val,
                                      }
                                    }));
                                  }}
                                />
                              )}
                            </td>
                            <td className="p-2 text-sm text-right"><Currency value={total} /></td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const rows = ((adjustedInvoice?.details?.receptionsByLaundry)||[]).filter(r => lavanderiaIds.includes(r.lavanderiaId));
                      const sums = rows.reduce((acc, r) => {
                        const kilos = includeKilos ? Number(r.kilos || 0) : 0;
                        const unit = 5;
                        const ov = receptionOverrides[r.lavanderiaId] || {};
                        const base = Number((kilos * unit).toFixed(2));
                        const fixed = Number(ov.fixedAmount || 0);
                        const sub = base;
                        const disc = ov.fixedEnabled ? Math.max(0, Number((base - fixed).toFixed(2))) : Number(ov.discount || 0);
                        const tot = ov.fixedEnabled ? fixed : Math.max(0, Number((base - disc).toFixed(2)));
                        acc.subtotal += sub;
                        acc.descuento += disc;
                        acc.total += tot;
                        return acc;
                      }, { subtotal: 0, descuento: 0, total: 0 });
                      return (
                        <tr className="bg-muted/30 font-semibold">
                          <td className="p-2 text-sm">Totales</td>
                          <td className="p-2 text-sm" colSpan={4}></td>
                          <td className="p-2 text-sm text-right"><Currency value={sums.subtotal} /></td>
                          <td className="p-2 text-sm text-right"><Currency value={sums.descuento} /></td>
                          <td className="p-2 text-sm text-right"><Currency value={sums.total} /></td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sección: Usuarios registro de horas */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios registro de horas</CardTitle>
        </CardHeader>
        <CardContent>
          {!invoice ? (
            <div className="text-sm text-muted-foreground">Sin datos para mostrar.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                {(() => {
                  const it = (adjustedInvoice.items||[]).find(i => i.code === 'WORKED_HOURS_USERS');
                  return <div className="text-sm">Incluidos: <span className="font-semibold">{it?.quantity ?? 0}</span></div>;
                })()}
                {(() => {
                  const it = (adjustedInvoice.items||[]).find(i => i.code === 'WORKED_HOURS_USERS');
                  return <div className="text-right text-sm">Subtotal: <span className="font-semibold"><Currency value={it?.amount ?? 0} /></span></div>;
                })()}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input id="inc-worked" type="checkbox" checked={includeWorked} onChange={(e)=>setIncludeWorked(e.target.checked)} />
                <label htmlFor="inc-worked">Incluir usuarios con registro de horas</label>
              </div>

              <div className="max-h-72 overflow-auto border rounded">
                <table className="min-w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 text-xs uppercase text-muted-foreground">Nombre</th>
                      <th className="text-left p-2 text-xs uppercase text-muted-foreground">Documento</th>
                      <th className="text-left p-2 text-xs uppercase text-muted-foreground">Rol</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Fichajes</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Excluir</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Descuento</th>
                      <th className="text-right p-2 text-xs uppercase text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(adjustedInvoice?.details?.workedUsers||[]).map(u => {
                      const isExcluded = excludedWorked.has(String(u.id)) || !!u.excluded;
                      const rowTotal = isExcluded ? 0 : 5000;
                      const rowDiscount = isExcluded ? 5000 : 0;
                      return (
                        <tr key={u.id} className="border-t">
                          <td className="p-2 text-sm">{u.nombre}</td>
                          <td className="p-2 text-sm">{u.cedula || '-'}</td>
                          <td className="p-2 text-sm">{u.rol || '-'}</td>
                          <td className="p-2 text-sm text-right">{u.count || 0}</td>
                          <td className="p-2 text-sm text-right">
                            <input type="checkbox" checked={excludedWorked.has(String(u.id))} onChange={(e)=>onToggleWorked(u.id, e.target.checked)} />
                          </td>
                          <td className="p-2 text-sm text-right"><Currency value={rowDiscount} /></td>
                          <td className="p-2 text-sm text-right"><Currency value={rowTotal} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const rows = (adjustedInvoice?.details?.workedUsers||[]);
                      const baseSubtotal = rows.length * 5000;
                      const discountTotal = rows.reduce((acc, u) => acc + ((excludedWorked.has(String(u.id)) || !!u.excluded) ? 5000 : 0), 0);
                      const total = Math.max(0, baseSubtotal - discountTotal);
                      return (
                        <tr className="bg-muted/30 font-semibold">
                          <td className="p-2 text-sm">Totales</td>
                          <td className="p-2 text-sm" colSpan={4}></td>
                          <td className="p-2 text-sm text-right"><Currency value={discountTotal} /></td>
                          <td className="p-2 text-sm text-right"><Currency value={total} /></td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sección: Usuarios administradores */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios administradores</CardTitle>
        </CardHeader>
        <CardContent>
          {!invoice ? (
            <div className="text-sm text-muted-foreground">Sin datos para mostrar.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                {(() => {
                  const admin = (adjustedInvoice.items||[]).find(i => i.code === 'USERS_ADMIN');
                  const sup = (adjustedInvoice.items||[]).find(i => i.code === 'USERS_SUPERVISOR');
                  return <div className="text-sm">Admins: <span className="font-semibold">{admin?.quantity ?? 0}</span> • Supervisores: <span className="font-semibold">{sup?.quantity ?? 0}</span></div>;
                })()}
                {(() => {
                  const admin = (adjustedInvoice.items||[]).find(i => i.code === 'USERS_ADMIN');
                  const sup = (adjustedInvoice.items||[]).find(i => i.code === 'USERS_SUPERVISOR');
                  const total = Number(admin?.amount || 0) + Number(sup?.amount || 0);
                  return <div className="text-right text-sm">Subtotal: <span className="font-semibold"><Currency value={total} /></span></div>;
                })()}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input id="inc-admins" type="checkbox" checked={includeAdmins} onChange={(e)=>setIncludeAdmins(e.target.checked)} />
                <label htmlFor="inc-admins">Incluir usuarios administradores y supervisores</label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded">
                  <div className="px-3 py-2 font-semibold bg-muted/50 border-b">Usuarios Admin</div>
                  <div className="max-h-64 overflow-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left p-2 text-xs uppercase text-muted-foreground">Nombre</th>
                          <th className="text-left p-2 text-xs uppercase text-muted-foreground">Correo</th>
                          <th className="text-right p-2 text-xs uppercase text-muted-foreground">Excluir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(adjustedInvoice?.details?.adminUsers||[]).map(u => (
                          <tr key={u.id} className="border-t">
                            <td className="p-2 text-sm">{u.nombre}</td>
                            <td className="p-2 text-sm">{u.correo || '-'}</td>
                            <td className="p-2 text-sm text-right">
                              <input type="checkbox" checked={excludedAdmins.has(String(u.id))} onChange={(e)=>onToggleAdmin(u.id, e.target.checked)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="border rounded">
                  <div className="px-3 py-2 font-semibold bg-muted/50 border-b">Usuarios Supervisor</div>
                  <div className="max-h-64 overflow-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left p-2 text-xs uppercase text-muted-foreground">Nombre</th>
                          <th className="text-left p-2 text-xs uppercase text-muted-foreground">Correo</th>
                          <th className="text-right p-2 text-xs uppercase text-muted-foreground">Excluir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(adjustedInvoice?.details?.supervisorUsers||[]).map(u => (
                          <tr key={u.id} className="border-t">
                            <td className="p-2 text-sm">{u.nombre}</td>
                            <td className="p-2 text-sm">{u.correo || '-'}</td>
                            <td className="p-2 text-sm text-right">
                              <input type="checkbox" checked={excludedSupervisors.has(String(u.id))} onChange={(e)=>onToggleSupervisor(u.id, e.target.checked)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
